/**
 * +grid: the Waystone Grid layout, built deterministically from the hand-authored recipes in data.ts
 * (no randomness: positions come from fixed polar offsets around each class angle, so the same build
 * always yields the same ~320 nodes, ids and links).
 *
 * The wheel (y down, angles clockwise from the top):
 *   - the Hearth Ring (r 150): one hub node per class plus a waypoint on each border, linking every
 *     region near the centre;
 *   - each class region (±22°): its gate (r 240), three spokes of travel nodes (r 275–390) ending in
 *     six-star clusters around a notable (r 430–525), two crosslinks, three slot modifiers, three
 *     utility stars;
 *   - each border (class angle + 36°): a nine-star blend cluster joined to both neighbours' spokes;
 *   - the middle ring (r 575–700): per class two lock nodes (sealed sigils, a Grid Key each), each
 *     guarding a four-star pocket with a greater notable;
 *   - the outer ring (r 760–830): keystones on three borders (reached from the neighbouring pockets),
 *     dormant stars on the other borders and the class angles (cap 30+).
 *
 * Later rings (≈700 nodes at cap 30, ≈1,200 long term) slot in beyond r 830 and between the pockets:
 * ids are namespaced (`<class>.…`, `border<i>.…`, `hub.…`, `ks.…`), so new nodes never rename old ones.
 */
import { GRID_BORDERS, GRID_CLASS_ORDER, GRID_DORMANT_KEYSTONES, GRID_IDLE_NAME, GRID_IDLE_VALUE, GRID_KEYSTONES, GRID_MIDDLE_MULT, GRID_NOTABLES, GRID_REGIONS, GRID_STAT_NAME, GRID_STAT_VALUE, slotNodeName } from './data';
import type { GridDef, GridEffect, GridIdle, GridNode, GridNodeId, GridNodeType, GridRing, GridStat, GridTheme, SlotMod, SlotSelector } from './types';

const RAD = Math.PI / 180;
const classAngle = (i: number): number => -90 + 72 * i;
const borderAngle = (i: number): number => classAngle(i) + 36;

interface Draft {
  nodes: GridNode[];
  byId: Map<GridNodeId, GridNode>;
}

function add(d: Draft, n: Omit<GridNode, 'links' | 'x' | 'y'> & { r: number; deg: number }): GridNode {
  if (d.byId.has(n.id)) throw new Error(`grid layout: duplicate node ${n.id}`);
  const { r, deg, ...rest } = n;
  const node: GridNode = { ...rest, x: Math.round(r * Math.cos(deg * RAD)), y: Math.round(r * Math.sin(deg * RAD)), links: [] };
  d.nodes.push(node);
  d.byId.set(node.id, node);
  return node;
}

function link(d: Draft, a: GridNodeId, b: GridNodeId): void {
  const x = d.byId.get(a);
  const y = d.byId.get(b);
  if (!x || !y) throw new Error(`grid layout: link ${a}–${b} to a missing node`);
  if (!x.links.includes(b)) x.links.push(b);
  if (!y.links.includes(a)) y.links.push(a);
}

const stat = (s: GridStat, mult = 1): GridEffect => ({ kind: 'stat', stat: s, value: Math.round(GRID_STAT_VALUE[s] * mult * 10000) / 10000 });
const idle = (s: GridIdle): GridEffect => ({ kind: 'idle', stat: s, value: GRID_IDLE_VALUE[s] });
const notableName = (id: string): string => GRID_NOTABLES.find((x) => x.id === id)?.name ?? id;
const keystoneName = (id: string): string => GRID_KEYSTONES.find((x) => x.id === id)?.name ?? id;

interface Common {
  ring: GridRing;
  region: string | null;
  theme: GridTheme;
}

function minor(d: Draft, id: string, c: Common, r: number, deg: number, s: GridStat, mult = 1, extra: Partial<GridNode> = {}): GridNode {
  return add(d, { id, name: GRID_STAT_NAME[s], type: 'minor', effects: [stat(s, mult)], ...c, r, deg, ...extra });
}

function slot(d: Draft, id: string, c: Common, r: number, deg: number, sel: SlotSelector, mod: SlotMod, extra: Partial<GridNode> = {}): GridNode {
  return add(d, { id, name: slotNodeName(sel, mod), type: 'slot', effects: [{ kind: 'slot', sel, mod }], ...c, r, deg, ...extra });
}

function notable(d: Draft, id: string, c: Common, r: number, deg: number, notableId: string, extra: Partial<GridNode> = {}): GridNode {
  return add(d, { id, name: notableName(notableId), type: 'notable', effects: [{ kind: 'notable', id: notableId }], ...c, r, deg, ...extra });
}

function utility(d: Draft, id: string, c: Common, r: number, deg: number, s: GridIdle): GridNode {
  return add(d, { id, name: GRID_IDLE_NAME[s], type: 'utility', effects: [idle(s)], ...c, r, deg });
}

function node(d: Draft, id: string, type: GridNodeType, name: string, c: Common, r: number, deg: number, extra: Partial<GridNode> = {}): GridNode {
  return add(d, { id, name, type, effects: [], ...c, r, deg, ...extra });
}

/** Build the grid (pure, deterministic). */
export function buildGrid(): GridDef {
  const d: Draft = { nodes: [], byId: new Map() };
  const gates: Record<string, GridNodeId> = {};

  // ---- class regions ---------------------------------------------------------
  GRID_REGIONS.forEach((rg, i) => {
    const A = classAngle(i);
    const C = rg.classId;
    const inner: Common = { ring: 'inner', region: C, theme: rg.theme };
    const hubC: Common = { ring: 'hub', region: C, theme: 'hearth' };
    const gate = node(d, `${C}.gate`, 'gate', rg.constellation, inner, 240, A, { gate: C, flavour: rg.flavour });
    gates[C] = gate.id;
    minor(d, `${C}.in`, inner, 190, A, rg.inward);
    minor(d, `${C}.hub`, hubC, 150, A, 'maxHp');
    link(d, gate.id, `${C}.in`);
    link(d, `${C}.in`, `${C}.hub`);
    utility(d, `${C}.u1`, inner, 205, A - 11, rg.utility[0]);
    utility(d, `${C}.u2`, inner, 205, A + 11, rg.utility[1]);
    link(d, `${C}.in`, `${C}.u1`);
    link(d, `${C}.in`, `${C}.u2`);

    const SPOKE = [
      { key: 'L', a: -14 },
      { key: 'C', a: 0 },
      { key: 'R', a: 14 },
    ] as const;
    SPOKE.forEach(({ key, a }, s) => {
      const sp = rg.spokes[s]!;
      const p = `${C}.${key}`;
      const deg = A + a;
      minor(d, `${p}.t1`, inner, 275, A + a / 2, sp.stats[0]);
      minor(d, `${p}.t2`, inner, 310, deg, sp.stats[1]);
      minor(d, `${p}.t3`, inner, 350, deg, sp.stats[0]);
      minor(d, `${p}.t4`, inner, 390, deg, sp.stats[1]);
      link(d, gate.id, `${p}.t1`);
      link(d, `${p}.t1`, `${p}.t2`);
      link(d, `${p}.t2`, `${p}.t3`);
      link(d, `${p}.t3`, `${p}.t4`);
      // The cluster: entry, a ring of four, the notable at the far end (two ways round).
      minor(d, `${p}.e`, inner, 430, deg, sp.ring);
      minor(d, `${p}.q1`, inner, 462, deg - 4, sp.stats[0]);
      minor(d, `${p}.q3`, inner, 462, deg + 4, sp.ring);
      minor(d, `${p}.q2`, inner, 496, deg - 4, sp.ring);
      minor(d, `${p}.q4`, inner, 496, deg + 4, sp.stats[0]);
      notable(d, `${p}.N`, inner, 528, deg, sp.notable);
      link(d, `${p}.t4`, `${p}.e`);
      link(d, `${p}.e`, `${p}.q1`);
      link(d, `${p}.e`, `${p}.q3`);
      link(d, `${p}.q1`, `${p}.q2`);
      link(d, `${p}.q3`, `${p}.q4`);
      link(d, `${p}.q2`, `${p}.N`);
      link(d, `${p}.q4`, `${p}.N`);
    });
    // Slot modifiers: off the left spoke, beyond the centre notable, off the right spoke.
    slot(d, `${C}.L.slot`, inner, 318, A - 22, rg.slots[0].sel, rg.slots[0].mod);
    link(d, `${C}.L.t2`, `${C}.L.slot`);
    slot(d, `${C}.C.slot`, inner, 560, A, rg.slots[1].sel, rg.slots[1].mod);
    link(d, `${C}.C.N`, `${C}.C.slot`);
    slot(d, `${C}.R.slot`, inner, 318, A + 22, rg.slots[2].sel, rg.slots[2].mod);
    link(d, `${C}.R.t2`, `${C}.R.slot`);
    // Crosslinks between the spokes, and the third utility star.
    minor(d, `${C}.xLC`, inner, 350, A - 7, rg.spokes[1].stats[0]);
    link(d, `${C}.L.t3`, `${C}.xLC`);
    link(d, `${C}.xLC`, `${C}.C.t3`);
    minor(d, `${C}.xCR`, inner, 350, A + 7, rg.spokes[1].stats[1]);
    link(d, `${C}.C.t3`, `${C}.xCR`);
    link(d, `${C}.xCR`, `${C}.R.t3`);
    utility(d, `${C}.u3`, inner, 398, A - 7, rg.utility[2]);
    link(d, `${C}.C.t4`, `${C}.u3`);

    // ---- middle ring: two locks, each guarding a pocket ----------------------
    (['A', 'B'] as const).forEach((side, j) => {
      const sgn = j === 0 ? -1 : 1;
      const pk = rg.pockets[j]!;
      const lockId = `${C}.lock${side}`;
      const mid: Common = { ring: 'middle', region: C, theme: rg.theme };
      node(d, lockId, 'lock', 'Sealed Sigil', mid, 578, A + sgn * 15);
      link(d, `${C}.${j === 0 ? 'L' : 'R'}.N`, lockId);
      const guard = { lockedBy: lockId };
      minor(d, `${C}.${side}.m1`, mid, 618, A + sgn * 15, pk.stats[0], GRID_MIDDLE_MULT, guard);
      // Pocket A: a minor and the slot; pocket B mirrored.
      minor(d, `${C}.${side}.m2`, mid, 658, A + sgn * 20, pk.stats[1], GRID_MIDDLE_MULT, guard);
      slot(d, `${C}.${side}.slot`, mid, 658, A + sgn * 10, pk.slot.sel, pk.slot.mod, guard);
      notable(d, `${C}.${side}.N`, mid, 702, A + sgn * 16, pk.notable, guard);
      link(d, lockId, `${C}.${side}.m1`);
      link(d, `${C}.${side}.m1`, `${C}.${side}.m2`);
      link(d, `${C}.${side}.m1`, `${C}.${side}.slot`);
      link(d, `${C}.${side}.m2`, `${C}.${side}.N`);
      link(d, `${C}.${side}.slot`, `${C}.${side}.N`);
    });
  });

  // ---- the Hearth Ring --------------------------------------------------------
  GRID_CLASS_ORDER.forEach((c, i) => {
    const next = GRID_CLASS_ORDER[(i + 1) % GRID_CLASS_ORDER.length]!;
    minor(d, `hub.w${i}`, { ring: 'hub', region: null, theme: 'hearth' }, 150, borderAngle(i), 'damage');
    link(d, `${c}.hub`, `hub.w${i}`);
    link(d, `hub.w${i}`, `${next}.hub`);
  });

  // ---- borders ----------------------------------------------------------------
  let dormantN = 0;
  GRID_BORDERS.forEach((b, i) => {
    const B = borderAngle(i);
    const left = GRID_CLASS_ORDER[i]!;
    const right = GRID_CLASS_ORDER[(i + 1) % GRID_CLASS_ORDER.length]!;
    const c: Common = { ring: 'border', region: null, theme: b.theme };
    const p = `border${i}`;
    minor(d, `${p}.bA`, c, 400, B - 11, b.stats[0]);
    minor(d, `${p}.bB`, c, 400, B + 11, b.stats[0]);
    link(d, `${left}.R.t4`, `${p}.bA`);
    link(d, `${right}.L.t4`, `${p}.bB`);
    minor(d, `${p}.k1`, c, 410, B - 4, b.stats[1]);
    minor(d, `${p}.k2`, c, 410, B + 4, b.stats[1]);
    link(d, `${p}.bA`, `${p}.k1`);
    link(d, `${p}.bB`, `${p}.k2`);
    link(d, `${p}.k1`, `${p}.k2`);
    utility(d, `${p}.u`, c, 362, B, b.utility);
    link(d, `${p}.k1`, `${p}.u`);
    link(d, `${p}.k2`, `${p}.u`);
    minor(d, `${p}.k3`, c, 452, B - 4, b.stats[0]);
    minor(d, `${p}.k4`, c, 452, B + 4, b.stats[1]);
    link(d, `${p}.k1`, `${p}.k3`);
    link(d, `${p}.k2`, `${p}.k4`);
    notable(d, `${p}.N`, c, 492, B, b.notable);
    link(d, `${p}.k3`, `${p}.N`);
    link(d, `${p}.k4`, `${p}.N`);
    slot(d, `${p}.slot`, c, 532, B, b.slot.sel, b.slot.mod);
    link(d, `${p}.N`, `${p}.slot`);

    // ---- the outer ring on this border: a keystone (active) or a dormant star ----
    const outer: Common = { ring: 'outer', region: null, theme: b.theme };
    if (b.keystone) {
      // The approach star sits between the two neighbouring pockets (behind either lock).
      minor(d, `ks.${b.keystone}.a`, outer, 768, B, b.stats[0], GRID_MIDDLE_MULT);
      link(d, `${left}.B.N`, `ks.${b.keystone}.a`);
      link(d, `${right}.A.N`, `ks.${b.keystone}.a`);
      add(d, { id: `ks.${b.keystone}`, name: keystoneName(b.keystone), type: 'keystone', effects: [{ kind: 'keystone', id: b.keystone }], ...outer, r: 834, deg: B });
      link(d, `ks.${b.keystone}.a`, `ks.${b.keystone}`);
    } else {
      const dk = GRID_DORMANT_KEYSTONES[dormantN++ % GRID_DORMANT_KEYSTONES.length]!;
      node(d, `dormant.border${i}`, 'dormant', dk.name, outer, 800, B, { flavour: dk.note });
      link(d, `${left}.B.N`, `dormant.border${i}`);
      link(d, `${right}.A.N`, `dormant.border${i}`);
    }
  });

  // Dormant stars beyond each class's pockets (future keystones).
  GRID_CLASS_ORDER.forEach((c, i) => {
    const dk = GRID_DORMANT_KEYSTONES[(dormantN + i) % GRID_DORMANT_KEYSTONES.length]!;
    const rg = GRID_REGIONS[i]!;
    node(d, `dormant.${c}`, 'dormant', dk.name, { ring: 'outer', region: c, theme: rg.theme }, 840, classAngle(i), { flavour: dk.note });
    link(d, `${c}.A.N`, `dormant.${c}`);
    link(d, `${c}.B.N`, `dormant.${c}`);
  });

  for (const n of d.nodes) n.links.sort();
  return { nodes: d.nodes, byId: d.byId, gates };
}
